/**
 * Link-card geometry + rung selection — PURE logic for F-31 (no React, no DOM,
 * no Yjs). Lives in the element-type folder per docs/08 §2: everything a link
 * card knows about itself is here or in its renderer; the canvas layer stays
 * type-agnostic.
 *
 * F-31(a), amended 2026-07-15 — DYNAMIC ASPECT RATIO: the card sizes itself to
 * the thumbnail's REAL intrinsic aspect (16:9 video, 1:1 avatar, tall poster)
 * instead of the fixed skeleton box every card used to keep. The server parses
 * the stored thumbnail's bytes and returns `thumbnailW`/`thumbnailH`; that pair
 * is persisted into the payload (`thumbW`/`thumbH`) and drives the card's `w`/
 * `h` when the unfurl lands.
 *
 * The card is a thumbnail band plus a text band, so the CARD's aspect is not
 * the thumbnail's: `h = w/ratio + bodyH`. Resize aspect-locking (F-32, shared
 * element machinery) then preserves the CARD's ratio, which is the ratio the
 * user actually sees — the text band scales with it rather than snapping.
 */

/** F-31(a): a card's width when it sizes itself to its thumbnail (board px). */
export const LINK_CARD_MAX_W = 320;

/**
 * Height of the text band under the thumbnail (board px), matched to the
 * `.link-card-body` padding + line box in app.css. Two rungs, because F-31(c)
 * gives a video card no domain line: title only.
 */
export const LINK_CARD_BODY_H = 52;
export const LINK_CARD_VIDEO_BODY_H = 40;

/**
 * Widest/narrowest thumbnail aspect we will honor. A provider that reports a
 * 1×2000 "thumbnail" (or a parse that produced a degenerate pair) must not
 * yield a card that is a 1-px-tall sliver or taller than the board is usable —
 * outside this band the caller keeps the skeleton size instead.
 */
const MIN_RATIO = 1 / 4;
const MAX_RATIO = 4;

/**
 * The card size for a thumbnail of intrinsic size `thumbW`×`thumbH`, or null
 * when the pair cannot be trusted (missing, non-finite, non-positive, or an
 * absurd aspect — see MIN_RATIO/MAX_RATIO). Null means "keep the size you
 * have": a card whose unfurl returned no thumbnail, or a nonsense one, stays
 * at its skeleton box rather than collapsing.
 *
 * `bodyH` is the text band's height (LINK_CARD_BODY_H / LINK_CARD_VIDEO_BODY_H).
 */
export function linkCardSizeForThumb(
  thumbW: number | undefined,
  thumbH: number | undefined,
  bodyH: number,
  maxW: number = LINK_CARD_MAX_W,
): { w: number; h: number } | null {
  if (thumbW === undefined || thumbH === undefined) return null;
  if (!Number.isFinite(thumbW) || !Number.isFinite(thumbH)) return null;
  if (thumbW <= 0 || thumbH <= 0) return null;
  const ratio = thumbW / thumbH;
  if (ratio < MIN_RATIO || ratio > MAX_RATIO) return null;
  const w = Math.round(maxW);
  // The thumbnail band renders at the card's full width, so its height is
  // w/ratio; the text band sits under it at a fixed height.
  const h = Math.round(w / ratio + bodyH);
  return { w: Math.max(1, w), h: Math.max(1, h) };
}

/** The subset of a link-card payload the pure helpers read. */
export interface LinkCardShape {
  thumbnailAssetId?: string | undefined;
  thumbW?: number | undefined;
  thumbH?: number | undefined;
  media?: "video" | undefined;
  provider?: string | undefined;
}

/**
 * F-31(b/c): does this card render as a VIDEO card (thumbnail + title only —
 * no domain line, no url line)?
 *
 * `media:"video"` is the real signal (the provider's oEmbed said `type:"video"`).
 * The provider-name check is a deliberate second rung: it catches cards written
 * by an older client (or a provider whose oEmbed omits `type`) whose thumbnail
 * and title are unmistakably a video's. Compared case-insensitively — oEmbed
 * `provider_name` is free-form prose ("YouTube"), not an identifier.
 */
export function isVideoCard(payload: LinkCardShape): boolean {
  if (payload.media === "video") return true;
  return payload.provider?.toLowerCase() === "youtube";
}

/** The text band height a card of this kind reserves (F-31c). */
export function linkCardBodyH(payload: LinkCardShape): number {
  return isVideoCard(payload) ? LINK_CARD_VIDEO_BODY_H : LINK_CARD_BODY_H;
}

/**
 * The size a card should take once its unfurl resolves, or null to keep the
 * current size. Thin composition of the two helpers above — the one entry
 * point the unfurl path calls, so the rung choice (video vs ordinary) can
 * never drift between the sizing and the renderer.
 */
export function linkCardSizeForPayload(payload: LinkCardShape): { w: number; h: number } | null {
  if (payload.thumbnailAssetId === undefined || payload.thumbnailAssetId === "") return null;
  return linkCardSizeForThumb(payload.thumbW, payload.thumbH, linkCardBodyH(payload));
}
